import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus, Settings2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { CalendarOccurrence } from '@/types/marketingProactive'
import { MarketingShell } from '../MarketingShell'
import { dateRange, daysAway, MOMENT_DOT, MOMENT_TONE, parseDay, toISODay } from './proactiveMeta'
import { MomentSheet } from './MomentSheet'
import { AddMomentDialog } from './AddMomentDialog'
import { ProactiveSettingsDialog } from './ProactiveSettingsDialog'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Long seasons and the monthly payday window sit in a strip above the grid
 *  instead of painting a chip on every day they cover. */
function isStripMoment(o: CalendarOccurrence): boolean {
  if (o.key === 'salary_week') return true
  return (parseDay(o.ends_on).getTime() - parseDay(o.starts_on).getTime()) / 86_400_000 >= 7
}

function monthGrid(month: Date): { start: Date; days: Date[] } {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const offset = (first.getDay() + 6) % 7 // Monday-first
  const start = new Date(first)
  start.setDate(first.getDate() - offset)
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  const tail = (7 - ((last.getDay() + 6) % 7) - 1 + 7) % 7
  const end = new Date(last)
  end.setDate(last.getDate() + tail)
  const days: Date[] = []
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) days.push(new Date(d))
  return { start, days }
}

/**
 * The marketing calendar (F-MKT-N1). Every promotable moment can become a
 * kit; Islamic dates are estimates until the owner confirms them; anything
 * irrelevant can be hidden for a year or for good.
 */
export function MarketingCalendar() {
  const [month, setMonth] = useState(() => {
    const t = new Date()
    return new Date(t.getFullYear(), t.getMonth(), 1)
  })
  const [showHidden, setShowHidden] = useState(false)
  // The sheet follows the live row, so a saved edit shows at once.
  const [picked, setPicked] = useState<CalendarOccurrence | null>(null)
  const [adding, setAdding] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const grid = useMemo(() => monthGrid(month), [month])
  const from = toISODay(grid.days[0])
  const to = toISODay(grid.days[grid.days.length - 1])

  const query = useQuery({
    queryKey: ['marketing', 'calendar', from, to],
    queryFn: () => apiClient.getMarketingCalendar(from, to),
    staleTime: 60_000,
  })
  const settingsQuery = useQuery({
    queryKey: ['marketing', 'proactive-settings'],
    queryFn: () => apiClient.getProactiveSettings(),
    staleTime: 60_000,
  })
  const today = query.data?.data?.today ?? toISODay(new Date())
  const fetched = query.data?.data?.occurrences ?? []
  const all = fetched.filter((o) => showHidden || !o.hidden)
  const selected = picked ? (fetched.find((o) => o.id === picked.id) ?? picked) : null
  const setSelected = setPicked

  const monthStart = toISODay(month)
  const monthEnd = toISODay(new Date(month.getFullYear(), month.getMonth() + 1, 0))
  const strip = all.filter((o) => isStripMoment(o) && o.starts_on <= monthEnd && o.ends_on >= monthStart)
  const dayMoments = all.filter((o) => !isStripMoment(o))
  const inMonth = all
    .filter((o) => o.starts_on <= monthEnd && o.ends_on >= monthStart && o.key !== 'salary_week')
    .sort((a, b) => a.starts_on.localeCompare(b.starts_on))

  const settings = settingsQuery.data?.data
  const hints: string[] = []
  if (settings && !settings.has_location) hints.push('Pick your city in Settings to get weather ideas.')
  if (settings && settings.settings.alerts_enabled && !settings.owner_email_set)
    hints.push('Add an owner email in Automations → Weekly digest to get new ideas by email.')

  const shift = (n: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1))
  const monthLabel = month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <MarketingShell
      title="Calendar"
      description="Festivals, national days, food days, seasons and paydays — with a nudge before each one"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings2 className="mr-2 h-4 w-4" />
            Settings
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add moment
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {hints.length > 0 && (
          <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            {hints.join(' ')}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="px-2" onClick={() => shift(-1)} aria-label="Previous month">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="min-w-40 text-center text-base font-semibold">{monthLabel}</h2>
                  <Button size="sm" variant="ghost" className="px-2" onClick={() => shift(1)} aria-label="Next month">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const t = new Date()
                      setMonth(new Date(t.getFullYear(), t.getMonth(), 1))
                    }}
                  >
                    Today
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="show-hidden" checked={showHidden} onCheckedChange={setShowHidden} />
                  <Label htmlFor="show-hidden" className="text-sm font-normal text-muted-foreground">
                    Show hidden
                  </Label>
                </div>
              </div>

              {strip.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {strip.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setSelected(o)}
                      className={cn(
                        'rounded-md px-2.5 py-1 text-xs font-medium transition-opacity hover:opacity-80',
                        MOMENT_TONE[o.kind] ?? MOMENT_TONE.custom,
                        o.hidden && 'opacity-50 line-through',
                      )}
                    >
                      {o.approximate ? '≈ ' : ''}
                      {o.title} · {dateRange(o.starts_on, o.ends_on)}
                    </button>
                  ))}
                </div>
              )}

              {query.isLoading ? (
                <Skeleton className="h-[480px] w-full" />
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <div className="grid grid-cols-7 border-b bg-muted/40">
                    {WEEKDAYS.map((d) => (
                      <div key={d} className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {d}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {grid.days.map((d) => {
                      const iso = toISODay(d)
                      const inThisMonth = d.getMonth() === month.getMonth()
                      const moments = dayMoments.filter((o) => o.starts_on <= iso && o.ends_on >= iso)
                      const nudges = dayMoments.filter((o) => o.promote && !o.hidden && o.nudge_on === iso && o.nudge_on !== o.starts_on)
                      return (
                        <div
                          key={iso}
                          className={cn(
                            'min-h-24 border-b border-r p-1.5 [&:nth-child(7n)]:border-r-0',
                            !inThisMonth && 'bg-muted/20',
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span
                              className={cn(
                                'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                                iso === today ? 'bg-primary font-semibold text-primary-foreground' : inThisMonth ? 'text-foreground' : 'text-muted-foreground/60',
                              )}
                            >
                              {d.getDate()}
                            </span>
                            {nudges.length > 0 && (
                              <span
                                className="h-1.5 w-1.5 rounded-full bg-primary"
                                title={`Nudge for ${nudges.map((n) => n.title).join(', ')}`}
                              />
                            )}
                          </div>
                          <div className="mt-1 space-y-1">
                            {moments.slice(0, 3).map((o) => (
                              <button
                                key={o.id}
                                onClick={() => setSelected(o)}
                                className={cn(
                                  'block w-full truncate rounded px-1.5 py-0.5 text-left text-xs font-medium transition-opacity hover:opacity-80',
                                  MOMENT_TONE[o.kind] ?? MOMENT_TONE.custom,
                                  o.hidden && 'opacity-50 line-through',
                                )}
                                title={o.title}
                              >
                                {o.approximate ? '≈ ' : ''}
                                {o.title}
                              </button>
                            ))}
                            {moments.length > 3 && (
                              <p className="px-1 text-xs text-muted-foreground">+{moments.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                ≈ Islamic dates follow the moon sighting — open one to confirm its date. A dot marks the day you'll be nudged.
              </p>
            </CardContent>
          </Card>

          <Card className="h-fit">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">In {month.toLocaleDateString('en-GB', { month: 'long' })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {inMonth.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing this month.</p>
              ) : (
                inMonth.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setSelected(o)}
                    className={cn('flex w-full items-start gap-3 rounded-md p-2 text-left hover:bg-muted/40', o.hidden && 'opacity-50')}
                  >
                    <span className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', MOMENT_DOT[o.kind] ?? MOMENT_DOT.custom)} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{o.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {dateRange(o.starts_on, o.ends_on)}
                        {o.days_until >= 0 && o.days_until <= 60 ? ` · ${daysAway(o.days_until)}` : ''}
                        {o.kit_status ? ` · kit ${o.kit_status === 'launched' ? 'live' : 'drafted'}` : ''}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <MomentSheet
        occurrence={selected}
        onOpenChange={(o) => !o && setSelected(null)}
        hiddenMoments={settings?.settings.hidden_moments ?? []}
        settings={settings?.settings}
      />
      <AddMomentDialog open={adding} onOpenChange={setAdding} />
      <ProactiveSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </MarketingShell>
  )
}
